import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { serviceBySlug } from "@/data/services";

const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(false));
  }, []);

  const cards = stats
    ? [
        { label: "Total bookings", value: stats.total_bookings },
        { label: "Pending", value: stats.pending },
        { label: "Confirmed", value: stats.confirmed },
        { label: "Completed", value: stats.completed },
        { label: "Paid bookings", value: stats.paid },
        { label: "Revenue collected", value: inr(stats.revenue_paise) },
        { label: "Active notices", value: stats.notices },
      ]
    : [];

  return (
    <AdminLayout title="Dashboard" testId="admin-dashboard">
      {stats === null && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="stats-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />
          ))}
        </div>
      )}
      {stats === false && (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="stats-error">
          Could not load dashboard stats.
        </p>
      )}
      {stats && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
            {cards.map((c) => (
              <div
                key={c.label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="font-display text-2xl font-bold tracking-tight text-slate-900">{c.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                  {c.label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
              Recent bookings
            </h2>
            <Link
              to="/admin/bookings"
              data-testid="dashboard-manage-bookings-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-900 hover:text-blue-700"
            >
              Manage all
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm" data-testid="recent-bookings-table">
              <thead>
                <tr className="border-b border-slate-200 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Service</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Payment</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_bookings.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-slate-500">
                      No bookings yet.
                    </td>
                  </tr>
                )}
                {stats.recent_bookings.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-slate-900">{b.name}</p>
                      <p className="text-xs text-slate-500">{b.email}</p>
                    </td>
                    <td className="px-5 py-3.5 text-slate-700">
                      {serviceBySlug(b.service)?.title || b.service}
                    </td>
                    <td className="px-5 py-3.5"><StatusBadge value={b.status} /></td>
                    <td className="px-5 py-3.5"><StatusBadge value={b.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
